import { useForm } from '@tanstack/react-form'
import { useMemo } from 'react'
import { useExpensesStore } from '../store/expensesStore'
import {
  ExpenseCreateSchema,
  ExpenseUpdateSchema,
} from '../schema/expensesSchema'
import { useUpdateExpenseMutation } from '../hooks/expenses/useUpdateExpenseMutation'
import { ExpenseItemsTable } from './ExpenseItemsTable'
import type {
  ExpenseItemData,
  ExpenseItemInput,
  ExpenseUpdateData,
} from '../schema/expensesSchema'
import type { ApiError } from '@/types/api'
import { AutoCompleteSelect } from '@/components/custom/auto-complete-select'
import CurrencyInput from '@/components/custom/currency-input'
import DatePicker from '@/components/custom/date-picker'
import { FormInput } from '@/components/custom/form-input'
import { FormTextarea } from '@/components/custom/form-textarea'
import LoadingButton from '@/components/custom/loading-button'
import { useChurchesStore } from '@/features/churches/store/churchesStore'
import { notifyRequestError } from '@/utils/notifyRequestError'
import { transformDateToTimezone } from '@/utils/timeZoneUtils'

interface ExpenseUpdateFormProps {
  expenseId: string | null
  onClose?: () => void
}

export function ExpenseUpdateForm({
  expenseId,
  onClose,
}: ExpenseUpdateFormProps) {
  const { mutateAsync: updateExpenseMutation, isPending } =
    useUpdateExpenseMutation()

  const churches = useChurchesStore((s) => s.churches)
  const expenses = useExpensesStore((s) => s.expenses)
  const categories = useExpensesStore((s) => s.categories)
  const expense = useMemo(
    () => expenses.find((e) => e._id === expenseId),
    [expenses, expenseId],
  )

  const form = useForm({
    defaultValues: {
      _id: expense?._id || '',
      churchId: expense?.churchId || '',
      requester: expense?.requester || '',
      requesterEmail: expense?.requesterEmail || '',
      amount: expense?.amount || 0,
      date: expense ? new Date(expense.date) : '',
      notes: expense?.notes || '',
      expenseItems:
        expense?.expenseItems ||
        ([] as Array<ExpenseItemData | ExpenseItemInput>),
    },
    onSubmit: async ({ value }) => {
      const totalAmount = value.expenseItems.reduce(
        (sum, item) => sum + item.amount,
        0,
      )

      const transformedValue = {
        ...value,
        amount: totalAmount,
        date: transformDateToTimezone(value.date),
      }

      try {
        await updateExpenseMutation({
          expense: transformedValue as ExpenseUpdateData,
        })
        form.reset()
        onClose?.()
      } catch (error) {
        console.log('Error occurred while submitting form:', error)
        notifyRequestError(
          error as ApiError,
          'Error occurred while submitting expense form',
        )
      }
    },
  })

  return (
    <div>
      <form
        id="donation-form"
        onSubmit={(e) => {
          e.preventDefault()
          e.stopPropagation()
          form.handleSubmit()
        }}
        className="space-y-2"
      >
        {/* Watch donorId changes and auto-update churchId */}
        <form.Field
          name="requester"
          validators={{
            onBlur: ({ value }) => {
              const result =
                ExpenseCreateSchema.shape.requester.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <FormInput
                  field={field}
                  label="Name"
                  id={field.name}
                  name={field.name}
                  required
                />
              </div>
            )
          }}
        />

        <form.Field
          name="requesterEmail"
          validators={{
            onBlur: ({ value }) => {
              const result =
                ExpenseCreateSchema.shape.requesterEmail.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => {
            return (
              <div className="gap-0">
                <FormInput
                  field={field}
                  label="Email"
                  id={field.name}
                  name={field.name}
                  required
                />
              </div>
            )
          }}
        />

        {/* Hidden churchId field for form validation */}
        <form.Field
          name="churchId"
          validators={{
            onBlur: ({ value }) => {
              const result = ExpenseCreateSchema.shape.churchId.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => (
            <div className="gap-0">
              <AutoCompleteSelect
                field={field}
                label="Church"
                id={field.name}
                name={field.name}
                options={churches.map((church) => ({
                  value: church._id,
                  label: church.name,
                }))}
                required
                disabled={isPending}
              />
            </div>
          )}
        />

        <div className="grid grid-cols-2 gap-4">
          <form.Field
            name="amount"
            validators={{
              onBlur: ({ value }) => {
                const result = ExpenseCreateSchema.shape.amount.safeParse(value)
                return result.success
                  ? undefined
                  : result.error.errors[0].message
              },
            }}
            children={(field) => {
              return (
                <div className="gap-0">
                  <CurrencyInput
                    field={field}
                    label="Amount"
                    id={field.name}
                    name={field.name}
                    className="text-right"
                    required
                  />
                </div>
              )
            }}
          />

          <form.Field
            name="date"
            children={(field) => (
              <div className="gap-0">
                <DatePicker
                  field={field}
                  label="Date"
                  id={field.name}
                  name={field.name}
                  startYear={2000}
                  disableFutureDates
                  required
                />
              </div>
            )}
          />
        </div>

        <form.Field
          name="notes"
          validators={{
            onBlur: ({ value }) => {
              const result = ExpenseCreateSchema.shape.notes.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => (
            <div className="gap-0">
              <FormTextarea
                field={field}
                label="Notes"
                id={field.name}
                name={field.name}
                disabled={isPending}
              />
            </div>
          )}
        />

        <form.Field
          name="expenseItems"
          validators={{
            onChange: ({ value }) => {
              const result =
                ExpenseUpdateSchema.shape.expenseItems.safeParse(value)
              return result.success ? undefined : result.error.errors[0].message
            },
          }}
          children={(field) => (
            <div className="gap-0">
              <ExpenseItemsTable
                items={field.state.value}
                categories={categories}
                disabled={isPending}
                onChange={(items) => field.handleChange(items)}
              />
              {field.state.meta.errors.length > 0 && (
                <p className="text-sm text-destructive mt-2">
                  {field.state.meta.errors[0]}
                </p>
              )}
            </div>
          )}
        />

        <form.Subscribe
          selector={(state) => [state.canSubmit, state.errors]}
          children={([canSubmit]) => (
            <LoadingButton
              type="submit"
              className="w-full mt-4"
              isLoading={isPending}
              disabled={!canSubmit || isPending}
            >
              {isPending ? 'Submitting...' : 'Submit'}
            </LoadingButton>
          )}
        />
      </form>
    </div>
  )
}
